'use client';

import { Button } from '@ui/components/button';
import { Checkbox } from '@ui/components/checkbox';
import { Input } from '@ui/components/input';
import { Label } from '@ui/components/label';
import { cn } from '@ui/lib/utils';
import { text } from '@modules/garden-planner/text';
import { useEffect } from 'react';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faGear } from '@micks-yard/icons/solid/faGear';
import { faMinus } from '@micks-yard/icons/solid/faMinus';
import { faPlus } from '@micks-yard/icons/solid/faPlus';
import { faXmark } from '@micks-yard/icons/solid/faXmark';

interface CellSettingsToolViewProps {
  isActive: boolean;
  selectedCell: { row: number; col: number } | null;
  slotsPerCell: number;
  getSlotsForCell: (row: number, col: number) => number;
  onToggle: () => void;
  onSelectCellClear: () => void;
  onSlotsPerCellChange: (slots: number) => void;
  onSaveCellSettings: () => Promise<void>;
  isBulkEditMode: boolean;
  onToggleBulkMode: (enabled: boolean) => void;
  bulkSelectionCount: number;
  onBulkSave: () => Promise<void>;
}

export function CellSettingsToolView({
  isActive,
  selectedCell,
  slotsPerCell,
  getSlotsForCell,
  onToggle,
  onSelectCellClear,
  onSlotsPerCellChange,
  onSaveCellSettings,
  isBulkEditMode,
  onToggleBulkMode,
  bulkSelectionCount,
  onBulkSave,
}: CellSettingsToolViewProps) {
  useEffect(() => {
    if (selectedCell && !isBulkEditMode) {
      onSlotsPerCellChange(getSlotsForCell(selectedCell.row, selectedCell.col));
    }
  }, [selectedCell, getSlotsForCell, onSlotsPerCellChange, isBulkEditMode]);

  const slotsStepperEl = (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm font-medium">{text.slotsPerCell}</Label>
      <div className="flex flex-row items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={slotsPerCell <= 1}
          onClick={() => onSlotsPerCellChange(slotsPerCell - 1)}
          aria-label={text.decreaseSlots}>
          <FaIcon icon={faMinus} className="size-4" />
        </Button>
        <Input
          className="h-7 flex-1 text-center"
          inputMode="numeric"
          value={String(slotsPerCell)}
          onChange={(event) => {
            const parsed = parseInt(event.target.value, 10);
            if (!isNaN(parsed) && parsed >= 1) {
              onSlotsPerCellChange(parsed);
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={slotsPerCell >= 5}
          onClick={() => onSlotsPerCellChange(slotsPerCell + 1)}
          aria-label={text.increaseSlots}>
          <FaIcon icon={faPlus} className="size-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="mb-3">
      <Button
        type="button"
        variant={isActive ? 'ghost' : 'outline'}
        className={cn(
          'w-full justify-start gap-2',
          isActive && 'bg-green-100 text-green-800 hover:bg-green-200 hover:text-green-800'
        )}
        onClick={onToggle}>
        <FaIcon icon={faGear} className="size-4" />
        {isActive ? text.exitCellSettings : text.editCellSettings}
      </Button>

      {isActive ? (
        <div className="bg-muted mt-2 flex flex-col gap-3 rounded-r-md border-l-4 border-l-green-600 py-3 pr-3 pl-3">
          <div className="flex cursor-pointer flex-row items-center gap-2">
            <Checkbox
              id="bulk-edit-mode"
              checked={isBulkEditMode}
              onCheckedChange={(checked) => onToggleBulkMode(checked === true)}
            />
            <Label htmlFor="bulk-edit-mode" className="cursor-pointer text-sm font-medium">
              {text.bulkEditMode}
            </Label>
          </div>

          {isBulkEditMode ? (
            <>
              <p className="text-muted-foreground text-sm">
                {bulkSelectionCount === 0
                  ? text.bulkClickCells
                  : text.bulkCellsSelected(bulkSelectionCount)}
              </p>
              {slotsStepperEl}
              <Button
                type="button"
                className="w-full"
                size="sm"
                disabled={bulkSelectionCount === 0}
                onClick={() => void onBulkSave()}>
                {text.saveToCells(bulkSelectionCount)}
              </Button>
            </>
          ) : selectedCell ? (
            <>
              <div className="flex flex-row items-center justify-between">
                <p className="text-foreground text-sm font-semibold">
                  {text.cellPosition(selectedCell.row + 1, selectedCell.col + 1)}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onSelectCellClear}
                  aria-label={text.clearSelection}>
                  <FaIcon icon={faXmark} className="size-4" />
                </Button>
              </div>
              {slotsStepperEl}
              <Button
                type="button"
                className="w-full"
                size="sm"
                onClick={() => void onSaveCellSettings()}>
                {text.saveCellSettings}
              </Button>
            </>
          ) : (
            <p className="text-muted-foreground text-sm">{text.clickCellToSelect}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
